import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import type { ReportFilters } from "@/features/reports/types";
import { useCalibrationDueReport, type CalibrationDueRow } from "@/features/assets/reliability";
import { formatDate } from "@/features/assets/format";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";

export const Route = createFileRoute("/_authenticated/reports/calibration-due")({
  component: CalibrationDueReportPage,
});

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

function CalibrationDueReportPage() {
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;

  const [filters, setFilters] = useState<ReportFilters>({});
  const [dueStatus, setDueStatus] = useState<"" | CalibrationDueRow["due_status"]>("");
  const [dueWithinDays, setDueWithinDays] = useState<number | "">("");
  const [requiredFilter, setRequiredFilter] = useState<"all" | "yes" | "no">("all");

  const branchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;

  const { data: allRows = [], isLoading } = useCalibrationDueReport({
    branchId,
    departmentId: filters.departmentId ?? null,
    assetType: filters.assetClass ?? null,
    requiredOnly: requiredFilter === "yes",
  });

  const rows = useMemo(() => {
    return allRows.filter((r) => {
      if (dueStatus && r.due_status !== dueStatus) return false;
      if (requiredFilter === "no" && r.due_status !== "Not Required") return false;
      if (dueWithinDays !== "" && r.due_status !== "Not Required") {
        const d = daysUntil((r as any).next_calibration_date);
        if (d == null || d > Number(dueWithinDays)) return false;
      }
      return true;
    });
  }, [allRows, dueStatus, dueWithinDays, requiredFilter]);

  const exportRows = useMemo(
    () => () =>
      rows.map((r) => ({
        Code: r.asset_code,
        Name: r.asset_name,
        Type: r.asset_type ?? "",
        Branch: r.branch_name ?? "",
        Department: r.department_name ?? "",
        Location: r.location_name ?? "",
        "Calibration Required": r.calibration_interval_value ? "Yes" : "No",
        Interval: r.calibration_interval_value ? `${r.calibration_interval_value} ${r.calibration_interval_unit}` : "",
        "Last Calibration": r.last_calibration_date ? formatDate(r.last_calibration_date) : "",
        "Next Due": (r as any).next_calibration_date ? formatDate((r as any).next_calibration_date) : "",
        "Due Status": r.due_status,
        "Reminder Days": r.calibration_reminder_days ?? "",
        Agency: r.calibration_agency ?? "",
        "Certificate Required": r.calibration_certificate_required ? "Yes" : "No",
      })),
    [rows],
  );

  return (
    <ReportShell
      id="R-54"
      title="Calibration Due Report"
      description="Assets with calibration due, overdue and upcoming schedule."
      filters={filters}
      onChange={setFilters}
      enabled={["branchId", "departmentId", "assetClass"]}
      exportRows={exportRows}
      rightActions={
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Due status</Label>
            <Select value={dueStatus || "all"} onValueChange={(v) => setDueStatus(v === "all" ? "" : (v as CalibrationDueRow["due_status"]))}>
              <SelectTrigger className="h-9 w-32"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
                <SelectItem value="Due Soon">Due Soon</SelectItem>
                <SelectItem value="Current">Current</SelectItem>
                <SelectItem value="Not Required">Not Required</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Due within (days)</Label>
            <Input
              type="number"
              min={0}
              max={730}
              value={dueWithinDays}
              onChange={(e) => setDueWithinDays(e.target.value === "" ? "" : Math.max(0, Number(e.target.value) || 0))}
              className="h-9 w-24"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Required</Label>
            <Select value={requiredFilter} onValueChange={(v) => setRequiredFilter(v as typeof requiredFilter)}>
              <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      }
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Required</TableHead>
                  <TableHead>Interval</TableHead>
                  <TableHead>Last Calibration</TableHead>
                  <TableHead>Next Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reminder (d)</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Certificate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.asset_id}>
                    <TableCell className="font-mono text-xs">
                      <Link to="/assets/$assetId" params={{ assetId: r.asset_id }} hash="calibration" className="text-primary hover:underline">
                        {r.asset_code}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">{r.asset_name}</TableCell>
                    <TableCell>{r.asset_type ?? "—"}</TableCell>
                    <TableCell>{r.branch_name ?? "—"}</TableCell>
                    <TableCell>{r.department_name ?? "—"}</TableCell>
                    <TableCell>{r.location_name ?? "—"}</TableCell>
                    <TableCell>{r.calibration_interval_value ? "Yes" : "No"}</TableCell>
                    <TableCell className="text-sm">
                      {r.calibration_interval_value
                        ? `${r.calibration_interval_value} ${r.calibration_interval_unit}`
                        : "—"}
                    </TableCell>
                    <TableCell className="text-sm">{r.last_calibration_date ? formatDate(r.last_calibration_date) : "—"}</TableCell>
                    <TableCell className="text-sm">{(r as any).next_calibration_date ? formatDate((r as any).next_calibration_date) : "—"}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          r.due_status === "Overdue"
                            ? "destructive"
                            : r.due_status === "Due Soon"
                              ? "secondary"
                              : "outline"
                        }
                      >
                        {r.due_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="tabular-nums">{r.calibration_reminder_days ?? "—"}</TableCell>
                    <TableCell>{r.calibration_agency ?? "—"}</TableCell>
                    <TableCell>{r.calibration_certificate_required ? "Yes" : "No"}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={14} className="py-8 text-center text-sm text-muted-foreground">
                      No assets match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </ReportShell>
  );
}
